import {Box, Chip, Divider, FormControl, InputLabel, MenuItem, Popover, Select, Stack, Typography, useTheme} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import FilterListIcon from "@mui/icons-material/FilterList";
import ServerSideDataTable from "@components/ServerSideDataTable";
import {useEffect, useMemo, useState} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {CustomCellRendererProps} from "ag-grid-react";
import {format} from "date-fns";
import Button from "@mui/material/Button";
import {useSnackbar} from "notistack";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface DripEnrollment {
    id: number
    email: string
    enrolled_at_ts: number
    emails_sent: string
    emails_sent_count: number
    has_replied: boolean
    is_active: boolean
    stop_reason: string | null
    stopped_at_ts: number | null
    last_email_sent_at_ts: number | null
}

interface PageData {
    enrollments: DripEnrollment[]
}

const STOP_REASON_LABELS: Record<string, string> = {
    replied: "Replied",
    purchased_plan: "Purchased Plan",
    unsubscribed: "Unsubscribed",
    sequence_complete: "Sequence Complete",
    manually_stopped: "Manually Stopped",
};

export default function AdminDripCampaign() {
    const theme = useTheme();
    const { user } = useAuth();
    const {enqueueSnackbar} = useSnackbar();

    const [pageData, setPageData] = useState<PageData>();
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const openFilter = (e: React.MouseEvent<HTMLElement>) => setFilterAnchor(e.currentTarget);
    const closeFilter = () => setFilterAnchor(null);
    const isFilterOpen = Boolean(filterAnchor);
    const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
    const [draftStatusFilter, setDraftStatusFilter] = useState("");
    const [exportLoading, setExportLoading] = useState(false);

    const fetchDripEnrollmentsQuery = useQuery({
        queryKey: ["fetchDripEnrollmentsQuery"],
        queryFn: () => authenticateAndFetchData("/admin/drip-campaign/"),
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (fetchDripEnrollmentsQuery.data) {
            setPageData(fetchDripEnrollmentsQuery.data.data as PageData);
        }
    }, [fetchDripEnrollmentsQuery.data]);

    const toggleDripMutation = useMutation({
        mutationFn: (enrollmentId: number) => authenticateAndPostData(`/admin/toggle-drip-enrollment/?enrollment_id=${enrollmentId}`, {}),
        onSuccess: () => {
            enqueueSnackbar("Drip sequence paused successfully!", {
				variant: "success",
			});
            fetchDripEnrollmentsQuery.refetch()
        },
        onError: (err: any) => {            
            enqueueSnackbar(err?.message || "Failed to stop drip campaign!", {
				variant: "error",
			});
        }
    });

    const filteredEnrollments = useMemo(() => {
        if (!pageData?.enrollments) return [];
        let rows = [...pageData.enrollments];
        if (appliedStatusFilter === "active") {
            rows = rows.filter((e) => e.is_active);
        } else if (appliedStatusFilter === "stopped") {
            rows = rows.filter((e) => !e.is_active);
        }
        return rows;
    }, [pageData?.enrollments, appliedStatusFilter]);

    function timestampFormatter(params: ValueFormatterParams) {
        if (params.value === null || params.value === undefined) return "—";
        return format(new Date(params.value), "do MMM y, h:mm a");
    }

    const StatusCell = (cellProps: CustomCellRendererProps) => {
        const enrollment = cellProps.data as DripEnrollment;
        if (enrollment.is_active) {
            return <Chip label="Active" color="success" size="small"/>;
        }
        const label = enrollment.stop_reason
            ? STOP_REASON_LABELS[enrollment.stop_reason] ?? enrollment.stop_reason
            : "Stopped";
        return <Chip label={label} color="default" size="small"/>;
    };

    const RepliedCell = (cellProps: CustomCellRendererProps) => {
        return cellProps.value
            ? <Chip label="Yes" color="info" size="small"/>
            : <Chip label="No" color="default" size="small" variant="outlined"/>;
    };

    const ActionCell = (cellProps: CustomCellRendererProps) => {
        const enrollment = cellProps.data as DripEnrollment;

        if (!enrollment.is_active && enrollment.stop_reason !== "manually_stopped") {
            return <Typography variant="caption" color="text.disabled">—</Typography>;
        }

        return (
            <Button 
                variant="contained" 
                color={enrollment.is_active ? "error" : "success"} // Red if active (Stop), Green if inactive (Resume)
                size="small"
                onClick={() => {
                    // const actionText = enrollment.is_active ? "stop" : "resume";
                    // if(window.confirm(`Are you sure you want to ${actionText} the drip sequence for ${enrollment.email}?`)) {
                        toggleDripMutation.mutate(enrollment.id); 
                    // }
                }}
            >
                {enrollment.is_active ? "Stop Drip" : "Resume"}
            </Button>
        );
    };

    const columnDefs: ColDef[] = [
        {field: "email", headerName: "Email", minWidth: 260},
        {field: "enrolled_at_ts", headerName: "Enrolled On", valueFormatter: timestampFormatter, minWidth: 200},
        {field: "emails_sent", headerName: "Emails Sent", minWidth: 120},
        {field: "has_replied", headerName: "Replied", cellRenderer: RepliedCell, minWidth: 100},
        {field: "is_active", headerName: "Status", cellRenderer: StatusCell, minWidth: 160},
        {field: "last_email_sent_at_ts", headerName: "Last Email Sent", valueFormatter: timestampFormatter, minWidth: 200},
        {field: "stopped_at_ts", headerName: "Stopped At", valueFormatter: timestampFormatter, minWidth: 200},
        {headerName: "Actions", cellRenderer: ActionCell, minWidth: 130},
    ];

    const DripColumns: CSVColumn<DripEnrollment>[] = [
        { header: "Email", accessor: "email" },
        { 
            header: "Enrolled On", 
            accessor: (row) => 
                row.enrolled_at_ts 
                    ? format(new Date(row.enrolled_at_ts), "yyyy-MM-dd HH:mm:ss") 
                    : "—" 
        },
        { header: "Emails Sent", accessor: "emails_sent" },
        { header: "Emails Sent Count", accessor: "emails_sent_count" },
        { header: "Replied", accessor: (row) => (row.has_replied ? "Yes" : "No") },
        { 
            header: "Status", 
            accessor: (row) => {
                if (row.is_active) return "Active";
                return row.stop_reason 
                    ? STOP_REASON_LABELS[row.stop_reason] ?? row.stop_reason 
                    : "Stopped";
            } 
        },
        { 
            header: "Stop Reason", 
            accessor: (row) => 
                row.stop_reason 
                    ? STOP_REASON_LABELS[row.stop_reason] ?? row.stop_reason 
                    : "—" 
        },
        { 
            header: "Last Email Sent At", 
            accessor: (row) => 
                row.last_email_sent_at_ts 
                    ? format(new Date(row.last_email_sent_at_ts), "yyyy-MM-dd HH:mm:ss") 
                    : "—" 
        },
        { 
            header: "Stopped At", 
            accessor: (row) => 
                row.stopped_at_ts 
                    ? format(new Date(row.stopped_at_ts), "yyyy-MM-dd HH:mm:ss") 
                    : "—" 
        },
    ];

    // 2. Export Click Handler
    const handleExportDripCampaign = () => {
        setExportLoading(true);

        try {            
            const success = exportToCSV(
                filteredEnrollments, 
                DripColumns,                 
                getExportFilename(user?.email ?? "unknown", `DripEnrollments${appliedStatusFilter || "all"}`)
            );

            if (success) {
                enqueueSnackbar("Drip campaign enrollments exported!", { variant: "success" });
            } else {
                enqueueSnackbar("No enrollments available to export.", { variant: "error" });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Error while exporting drip campaign enrollments.", { variant: "error" });
        } finally {
            setExportLoading(false);
        }
    };

    const ExportDripCampaignCsvButton = () => {
        return (
            <Button startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={() => {
                    handleExportDripCampaign();
                }}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : "Export"}
            </Button>
        )
    }

    const TableActions = () => (
        <Stack direction="row" spacing={1}>
            <IconButton onClick={openFilter}>
                <FilterListIcon/>
            </IconButton>
            <ExportDripCampaignCsvButton />
        </Stack>
    );

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (fetchDripEnrollmentsQuery.isLoading) {
        return <PageLoading/>;
    } else if (fetchDripEnrollmentsQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={fetchDripEnrollmentsQuery.error as unknown as ApiRequestFailed}/>;
    } else if (pageData) {
        return (
            <Stack direction="column" spacing={2} sx={{pb: 4, height: "100%"}}>
                <Typography fontWeight="bold" variant="h5">Free Plan Drip Campaign</Typography>
                <Stack direction="row" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Total enrolled: {pageData.enrollments.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Active: {pageData.enrollments.filter(e => e.is_active).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Replied: {pageData.enrollments.filter(e => e.has_replied).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Unsubscribed: {pageData.enrollments.filter(e => e.stop_reason === "unsubscribed").length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manually stopped: {pageData.enrollments.filter(e => e.stop_reason === "manually_stopped").length}
                    </Typography>
                </Stack>
                <Divider/>
                <Box sx={{height: "100%"}}>
                    <ServerSideDataTable
                        columns={columnDefs}
                        rows={filteredEnrollments}
                        actions={TableActions()}
                    />
                </Box>
                <Popover
                    open={isFilterOpen}
                    anchorEl={filterAnchor}
                    onClose={closeFilter}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    PaperProps={{sx: {p: 2, width: 240}}}
                >
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={draftStatusFilter}
                                label="Status"
                                onChange={(e) => setDraftStatusFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="stopped">Stopped</MenuItem>
                            </Select>
                        </FormControl>
                        <Stack direction="row" justifyContent="space-between">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setDraftStatusFilter("");
                                    setAppliedStatusFilter("");
                                    closeFilter();
                                }}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setAppliedStatusFilter(draftStatusFilter);
                                    closeFilter();
                                }}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </Stack>
                </Popover>
            </Stack>
        );
    } else {
        return <></>;
    }
}
